 import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "./Bulk.css";

// Reusable Multi-Select Component with Tags and "No data available" panel
const CustomMultiSelect = ({
  label,
  options = [],
  selectedValues = [],
  onChange,
  displayKey = "name",
  valueKey = "_id",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = options.filter((item) =>
    item[displayKey]?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isAllSelected =
    options.length > 0 && selectedValues.length === options.length;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      onChange(options);
    } else {
      onChange([]);
    }
  };

  const handleOptionToggle = (option) => {
    const exists = selectedValues.some(
      (item) => item[valueKey] === option[valueKey]
    );
    if (exists) {
      onChange(
        selectedValues.filter((item) => item[valueKey] !== option[valueKey])
      );
    } else {
      onChange([...selectedValues, option]);
    }
  };

  const handleRemoveTag = (e, option) => {
    e.stopPropagation();
    onChange(
      selectedValues.filter((item) => item[valueKey] !== option[valueKey])
    );
  };

  return (
    <div className="select-container" ref={dropdownRef}>
      <label className="select-label">{label}</label>

      {/* Select Control Box */}
   {/* Select Control Box */}
<div className="select-box" onClick={() => setIsOpen(!isOpen)}>
  <div className="selected-tags-wrapper">
    {selectedValues.length === 0 ? (
      <span className="select-placeholder">Select</span>
    ) : (
      <>
        {/* Render maximum 2 tags inside the box */}
        {selectedValues.slice(0, 2).map((val) => (
          <span key={val[valueKey]} className="select-tag">
            {val[displayKey]}
            <button
              type="button"
              className="tag-remove-btn"
              onClick={(e) => handleRemoveTag(e, val)}
            >
              ×
            </button>
          </span>
        ))}

        {/* Show overflow badge if more than 2 items selected */}
        {selectedValues.length > 2 && (
          <span className="select-tag-more">
            +{selectedValues.length - 2} more
          </span>
        )}
      </>
    )}
  </div>
  <span className={`caret ${isOpen ? "up" : "down"}`}>▼</span>
</div>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="dropdown-panel">
          {options.length === 0 ? (
            <div className="no-data-msg">No data available</div>
          ) : (
            <>
              {/* Select All */}
              <label className="dropdown-item border-bottom">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                />
                <span>Select All</span>
              </label>

              {/* Search Box */}
              <div className="search-wrapper">
                <input
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              {/* Options List */}
              <div className="options-list">
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option) => {
                    const isChecked = selectedValues.some(
                      (item) => item[valueKey] === option[valueKey]
                    );
                    return (
                      <label
                        key={option[valueKey]}
                        className="dropdown-item"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleOptionToggle(option)}
                        />
                        <span>{option[displayKey]}</span>
                      </label>
                    );
                  })
                ) : (
                  <div className="no-data-msg">No data available</div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Main Component
const Bulk = () => {
  const [token, setToken] = useState("");
  const [user, setUser] = useState(null);

  // Dropdown Lists
  const [locations, setLocations] = useState([]);
  const [banks, setBanks] = useState([]);
  const [documents, setDocuments] = useState([]);

  // Selected State
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedBanks, setSelectedBanks] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);

  // Results & Reallocate State
  const [resultList, setResultList] = useState([]);
  const [selectedTableItems, setSelectedTableItems] = useState([]);
  const [verifierList, setVerifierList] = useState([]);
  const [fromVerifier, setFromVerifier] = useState("");
  const [toVerifiers, setToVerifiers] = useState([]);

  

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    const storedUser = localStorage.getItem("currentUser");
    const storedLocations = localStorage.getItem("locations");

    if (storedToken) setToken(storedToken);

    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error("USER JSON PARSE ERROR:", error);
      }
    }

    if (storedLocations) {
      try {
        const parsedLocations = JSON.parse(storedLocations);
        if (Array.isArray(parsedLocations)) {
          setLocations(parsedLocations);
        } else {
          setLocations([]);
        }
      } catch (error) {
        console.error("LOCATION JSON PARSE ERROR:", error);
        setLocations([]);
      }
    }
  }, []);

  useEffect(() => {
    if (token) {
      getVerifiers();
    }
  }, [token]);

  const getVerifiers = async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_ADMINNODE}/api/v1/admin/getUsersByRole`,
        { role: "verifier" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.status === 100) {
        const mapped = (res.data.result || []).map((v) => ({
          _id: v._id,
          name: `${v.firstName || ""} ${v.lastName || ""}/${v.district || ""}`,
          verifierCity: v.city,
        }));
        setVerifierList(mapped);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleLocationChange = (newSelected) => {
    setSelectedLocations(newSelected);
    setSelectedBanks([]);
    setSelectedDocuments([]);
    setBanks([]);
    setDocuments([]);

    if (newSelected.length > 0) {
      const locationIds = newSelected.map((location) => location.locationId);
      loadBanks(locationIds);
    }
  };
const loadBanks = async (locationIds) => {
  debugger;

  console.log("TOKEN:", token);
  console.log("LOCATION IDS:", locationIds);

  try {
    const res = await axios.post(
      `${process.env.REACT_APP_NODESERVER}/api/v1/document/manage/getUserBankListByLocation`,
      { locationIds },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    console.log("RESPONSE:", res.data);

    if (res.data?.status === 100) {
      setBanks(
        res.data.result.map((b) => ({
          _id: b._id,
          bankName: b.bankName,
        }))
      );
    }
  } catch (err) {
    console.error("BANK ERROR:", err.response?.data || err);
  }
};

const handleBankChange = (newSelected) => {
  setSelectedBanks(newSelected);
  setSelectedDocuments([]);

  const bankIds = newSelected.map((bank) => bank._id);

  if (bankIds.length > 0) {
    loadDocuments(bankIds);
  } else {
    setDocuments([]);
  }
};

const loadDocuments = async (bankIds) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_NODESERVER}/api/v1/document/manage/getDocumentTypeListMinimum`,
      { bankIds },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    console.log("DOCUMENT RESPONSE:", res.data);

    setDocuments(res.data || []);

  } catch (err) {
    console.error(
      "DOCUMENT ERROR:",
      err.response?.data || err
    );
  }
};
  const fetchData = async () => {
    const body = {
      bankIds: selectedBanks.map((b) => b._id),
      masterDocumentIds: selectedDocuments.map((d) => d._id),
    };

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_NODESERVER}/api/v1/document/manage/verifierDocCount`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data && res.data.status === 1) {
        setResultList(res.data.data || []);
        setSelectedTableItems([]);
        if ((res.data.data || []).length === 0) {
          Swal.fire("No data found!");
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSelectItem = (item, checked) => {
    if (checked) {
      setSelectedTableItems((prev) => [...prev, item]);
    } else {
      setSelectedTableItems((prev) =>
        prev.filter((i) => i.verifierId !== item.verifierId)
      );
    }
  };

  const submitReallocation = async () => {
    const payload = {
      fromVerifierId: [fromVerifier],
      toVerifierId: toVerifiers.map((v) => ({
        verifier: v.name,
        verifierId: v._id,
        verifierCity: v.verifierCity,
      })),
      bankIds: selectedBanks.map((b) => b._id),
      masterDocumentIds: selectedDocuments.map((d) => d._id),
      assignedBy: `verifier-${user?.firstName || ""} ${user?.lastName || ""}`,
    };

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_NODESERVER}/api/v1/document/manage/verifierBulkAssign`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data && res.data.status === 1) {
        Swal.fire("success");
        setSelectedLocations([]);
        setSelectedBanks([]);
        setSelectedDocuments([]);
        setResultList([]);
        setSelectedTableItems([]);
        setFromVerifier("");
        setToVerifiers([]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="page-wrapper">
      {/* Top Filter Card */}
      <div className="card-box">
        <div className="filter-grid">
          <CustomMultiSelect
            label="Locations"
            options={locations}
            selectedValues={selectedLocations}
            onChange={handleLocationChange}
            displayKey="locationName"
            valueKey="locationId"
          />

          <CustomMultiSelect
            label="Banks"
            options={banks}
            selectedValues={selectedBanks}
            onChange={handleBankChange}
            displayKey="bankName"
            valueKey="_id"
          />

          <CustomMultiSelect
            label="Documents"
            options={documents}
            selectedValues={selectedDocuments}
            onChange={(val) => setSelectedDocuments(val)}
            displayKey="documentTypeName"
            valueKey="_id"
          />
        </div>

        <div className="btn-container">
          <button className="btn-fetch" onClick={fetchData}>
            Fetch Data
          </button>
        </div>
      </div>

      {/* Results & Bulk Reallocate Portion */}
      {resultList.length > 0 && (
        <div className="card-box mt-20">
          <table className="custom-table">
            <thead>
              <tr>
                <th style={{ width: "40px" }}></th>
                <th>Verifier</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {resultList.map((item) => (
                <tr key={item.verifierId}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedTableItems.some(
                        (i) => i.verifierId === item.verifierId
                      )}
                      onChange={(e) =>
                        handleSelectItem(item, e.target.checked)
                      }
                    />
                  </td>
                  <td>{item.verifierName}</td>
                  <td>{item.count}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="reallocate-panel">
            <h3>Bulk Reallocate</h3>

            <div className="form-group">
              <label className="select-label">From Verifier</label>
              <select
                className="native-select"
                value={fromVerifier}
                onChange={(e) => setFromVerifier(e.target.value)}
              >
                <option value="">Select</option>
                {selectedTableItems.map((v) => (
                  <option key={v.verifierId} value={v.verifierId}>
                    {v.verifierName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group mt-10">
              <CustomMultiSelect
                label="To Verifiers"
                options={verifierList}
                selectedValues={toVerifiers}
                onChange={(val) => setToVerifiers(val)}
                displayKey="name"
                valueKey="_id"
              />
            </div>

            <button
              className="btn-reallocate"
              onClick={submitReallocation}
              disabled={!fromVerifier || selectedTableItems.length === 0}
            >
              Reallocate
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bulk;